<app-legal-page-layout
  kicker="Legal"
  title="Terms of Service"
  description="The legal agreement governing your use of the Safeword platform and Services."
  [metaLines]="['Operated by Hove Retail Limited']"
  [sections]="sections">

  <section id="account-registration">
    <h2>Account Registration and Eligibility</h2>
    <p>To register an account and use the Services, you must:</p>
    <ul>
      <li>be at least 18 years old;</li>
      <li>have the legal capacity to enter into a binding contract;</li>
      <li>provide accurate, current and complete registration information;</li>
      <li>complete any age verification or identity verification steps we require; and</li>
      <li>not be barred from using the Services under the laws of your jurisdiction or under any previous suspension or termination by Safeword.</li>
    </ul>
    <p>
      You are responsible for maintaining the confidentiality of your account credentials and for all activity that
      occurs under your account. You must notify us promptly if you become aware of any unauthorised use of your
      account.
    </p>
    <p>
      We may refuse to register an account, or may suspend or close an existing account, where we reasonably believe
      these eligibility requirements are not met.
    </p>
  </section>

  <section id="acceptable-use">
    <h2>Acceptable Use</h2>
    <p>In using the Services, you agree that you will not:</p>
    <ul>
      <li>breach any applicable law, regulation, or third party's rights;</li>
      <li>
        upload, share or facilitate access to any content prohibited under Section 3.6 (Prohibited Content) of these
        Terms or our Community Guidelines;
      </li>
      <li>harass, threaten, defame or abuse another member;</li>
      <li>impersonate any person or misrepresent your affiliation with any person or organisation;</li>
      <li>
        interfere with, disrupt or attempt to gain unauthorised access to the Services, other members' accounts, or
        our systems and networks;
      </li>
      <li>
        use automated means (bots, scrapers, or similar tools) to access or extract data from the Services without
        our prior written consent;
      </li>
      <li>circumvent or attempt to circumvent age verification, content moderation, or security measures; or</li>
      <li>use the Services for any unlawful, fraudulent or unauthorised commercial purpose.</li>
    </ul>
    <p>
      We may investigate suspected breaches of this Acceptable Use section and take enforcement action in accordance
      with Section 5 (Termination and Suspension of Accounts) and our
      <a routerLink="/legal/community-guidelines">Community Guidelines</a>.
    </p>
  </section>

  <section id="user-generated-content">
    <h2>User-Generated Content and Creator Responsibilities</h2>

    <h3>3.1 Overview</h3>
    <p>
      Safeword is a community-driven platform that enables members to create, upload, share, purchase and interact
      with user-generated content. <em>(Applies once paid subscriptions/marketplace features launch.)</em>
    </p>
    <p>
      Members remain responsible for the content they upload, publish, distribute or otherwise make available
      through the Services.
    </p>
    <p>
      Safeword is committed to supporting lawful adult expression while maintaining a safe, respectful and legally
      compliant environment for all members.
    </p>

    <h3>3.2 Ownership of Content</h3>
    <p>
      Unless otherwise agreed in writing, members retain ownership of the intellectual property rights in the
      content they create and upload to Safeword.
    </p>
    <p>Uploading content to Safeword does not transfer ownership of that content to Hove Retail Limited.</p>
    <p>
      Members remain responsible for ensuring they have the legal rights necessary to upload, publish, distribute or
      monetise their content.
    </p>

    <h3>3.3 Licence Granted to Safeword</h3>
    <p>
      By uploading or publishing content through Safeword, you grant Hove Retail Limited a non-exclusive, worldwide,
      royalty-free licence to:
    </p>
    <ul>
      <li>host your content;</li>
      <li>store your content;</li>
      <li>reproduce your content where technically necessary;</li>
      <li>display your content in accordance with your privacy settings;</li>
      <li>distribute your content to authorised users;</li>
      <li>cache and back up your content;</li>
      <li>create technical copies required for platform functionality;</li>
      <li>moderate and review your content;</li>
      <li>detect prohibited or unlawful material;</li>
      <li>optimise content for different devices and network conditions; and</li>
      <li>take any other technical steps reasonably necessary to operate, secure and improve the Services.</li>
    </ul>
    <p>This licence exists solely for the purpose of operating, maintaining, protecting and improving the Safeword platform.</p>
    <p>
      It ends when your content is permanently deleted from our active systems, subject to any legal obligations,
      backup retention periods or lawful preservation requirements.
    </p>

    <h3>3.4 Creator Responsibility</h3>
    <p>Creators are responsible for ensuring that all content they upload complies with:</p>
    <ul>
      <li>applicable laws;</li>
      <li>our Terms of Service;</li>
      <li>our Community Standards;</li>
      <li>payment processor requirements; <em>(Applies once paid subscriptions/marketplace features launch.)</em></li>
      <li>intellectual property laws; and</li>
      <li>all applicable safeguarding and age verification requirements.</li>
    </ul>
    <p>Creators remain responsible for all content published through their accounts.</p>

    <h3>3.5 Age and Consent</h3>
    <p>Every member uploading photographs, videos or other visual content confirms that:</p>
    <ul>
      <li>every identifiable individual depicted is at least 18 years of age;</li>
      <li>every identifiable individual freely and knowingly consented to the creation of the content;</li>
      <li>
        every identifiable individual freely and knowingly consented to the upload and publication of the content on
        Safeword;
      </li>
      <li>no individual has been coerced, exploited or deceived into participating;</li>
      <li>the uploader has the legal right to publish the content; and</li>
      <li>the content complies with applicable law.</li>
    </ul>
    <p>Where legally required, Safeword may request additional evidence to verify age, identity or consent.</p>
    <p>
      Failure to provide such information may result in content removal, suspension of monetisation privileges or
      account suspension. <em>(Applies once paid subscriptions/marketplace features launch.)</em>
    </p>

    <h3>3.6 Prohibited Content</h3>
    <p>Content that breaches our Terms of Service, Community Standards or applicable law may be removed without notice.</p>
    <p>Examples of prohibited content include, but are not limited to:</p>
    <ul>
      <li>child sexual abuse material (CSAM);</li>
      <li>any depiction involving persons under the age of 18;</li>
      <li>non-consensual intimate images;</li>
      <li>revenge pornography;</li>
      <li>coerced or exploitative sexual content;</li>
      <li>human trafficking or exploitation;</li>
      <li>bestiality;</li>
      <li>incest where prohibited by law;</li>
      <li>content promoting terrorism or violent extremism;</li>
      <li>fraud or scams;</li>
      <li>copyright infringement;</li>
      <li>impersonation;</li>
      <li>malicious software;</li>
      <li>unlawful commercial activity; and</li>
      <li>any other material prohibited by law or our platform policies.</li>
    </ul>
    <p>Safeword reserves the right to report suspected unlawful content to the appropriate authorities where required or permitted by law.</p>

    <h3>3.7 Artificial Intelligence and Manipulated Media</h3>
    <p>
      Members must not upload or distribute manipulated or artificially generated intimate images or videos of
      identifiable individuals without their explicit permission.
    </p>
    <p>This includes:</p>
    <ul>
      <li>AI-generated intimate images of real people;</li>
      <li>deepfake pornography;</li>
      <li>manipulated videos intended to deceive;</li>
      <li>digitally altered images designed to impersonate another individual; and</li>
      <li>other synthetic media that infringes privacy, intellectual property rights or applicable law.</li>
    </ul>
    <p>Safeword may remove such content immediately and take enforcement action against the responsible account.</p>

    <h3>3.8 Copyright and Intellectual Property</h3>
    <p>Members must upload only content that they own or have permission to use.</p>
    <p>Uploading copyrighted material without appropriate permission may result in:</p>
    <ul>
      <li>removal of the content;</li>
      <li>suspension of creator privileges;</li>
      <li>account suspension;</li>
      <li>permanent account termination; and</li>
      <li>legal action where appropriate.</li>
    </ul>
    <p>Safeword respects intellectual property rights and operates procedures for receiving and investigating copyright complaints.</p>

    <h3>3.9 Creator Verification</h3>
    <p>Creators wishing to monetise content may be required to complete additional verification procedures.</p>
    <p>These procedures may include:</p>
    <ul>
      <li>identity verification;</li>
      <li>age verification;</li>
      <li>payment verification;</li>
      <li>tax information where legally required;</li>
      <li>bank account verification;</li>
      <li>fraud prevention checks; and</li>
      <li>compliance checks required by payment processors or regulators.</li>
    </ul>
    <p>
      Safeword reserves the right to suspend creator accounts where verification cannot be completed or maintained.
      <em>(Applies once paid subscriptions/marketplace features launch.)</em>
    </p>

    <h3>3.10 Record Keeping</h3>
    <p>Where required by law, regulation or payment processor requirements, creators may be required to demonstrate:</p>
    <ul>
      <li>proof of age;</li>
      <li>proof of identity;</li>
      <li>proof of ownership of content;</li>
      <li>proof of consent from all identifiable participants; and</li>
      <li>other documentation reasonably required to investigate complaints or demonstrate compliance.</li>
    </ul>
    <p>Failure to cooperate with reasonable compliance requests may result in enforcement action.</p>

    <h3>3.11 Moderation and Enforcement</h3>
    <p>Safeword uses AI-assisted systems and trained human moderators to review content that is:</p>
    <ul>
      <li>reported by members;</li>
      <li>identified by automated systems;</li>
      <li>suspected of breaching our policies; or</li>
      <li>required to be reviewed for legal or regulatory purposes.</li>
    </ul>
    <p>Enforcement action may include:</p>
    <ul>
      <li>educational warnings;</li>
      <li>temporary content restriction;</li>
      <li>removal of content;</li>
      <li>demonetisation; <em>(Applies once paid subscriptions/marketplace features launch.)</em></li>
      <li>suspension of creator privileges;</li>
      <li>temporary account suspension; or</li>
      <li>permanent account termination.</li>
    </ul>
    <p>The action taken will depend on the seriousness, frequency and circumstances of the breach.</p>

    <h3>3.12 Repeat Violations</h3>
    <p>Members who repeatedly breach our Terms of Service or Community Standards may be subject to progressively stronger enforcement measures.</p>
    <p>Serious or repeated violations may result in permanent removal from the platform without further warning.</p>
    <p>
      Safeword reserves the right to refuse future registrations from individuals who have previously been
      permanently removed for serious misconduct.
    </p>

    <h3>3.13 Takedown Requests</h3>
    <p>
      Individuals who believe content infringes their privacy, intellectual property rights or other legal rights
      may submit a takedown request through the procedures published by Safeword.
    </p>
    <p>Where appropriate, we may request additional information to verify the identity of the complainant or assess the validity of the request.</p>
    <p>Content may be temporarily restricted while an investigation is carried out.</p>

    <h3>3.14 Removal of Content</h3>
    <p>Safeword reserves the right to remove, restrict, suspend or otherwise limit access to content where we reasonably believe that:</p>
    <ul>
      <li>it breaches our Terms of Service;</li>
      <li>it breaches our Community Standards;</li>
      <li>it infringes the rights of others;</li>
      <li>it creates an unacceptable risk to users;</li>
      <li>it is unlawful; or</li>
      <li>removal is otherwise necessary to protect Safeword, its members or the public.</li>
    </ul>
    <p>Nothing in these Terms of Service limits our rights to enforce these Terms or comply with applicable law.</p>

    <h3>3.15 Community Responsibility</h3>
    <p>
      Safeword is built upon the principles of informed consent, mutual respect, privacy, personal responsibility and
      lawful adult expression.
    </p>
    <p>Every member contributes to maintaining a safe and trusted community.</p>
    <p>
      By using Safeword, members acknowledge their responsibility to act honestly, respect the rights of others,
      comply with applicable laws and support a culture of consent, dignity and accountability.
    </p>
  </section>

  <section id="intellectual-property">
    <h2>Intellectual Property (Platform and Trade Marks)</h2>
    <p>
      Except for member-owned content licensed to us under Section 3.3, all intellectual property rights in the
      Safeword platform itself — including its software, design, layout, graphics, logos, and the "Safeword" name and
      trade marks — belong to Hove Retail Limited or our licensors.
    </p>
    <p>
      You may not copy, modify, distribute, sell, or create derivative works based on the platform (as distinct from
      your own content) without our prior written consent, except as permitted by mandatory law or as reasonably
      necessary to use the Services as intended.
    </p>
  </section>

  <section id="termination-suspension">
    <h2>Termination and Suspension of Accounts</h2>
    <p>
      You may close your account at any time through your account settings or by contacting us; see
      <a routerLink="/legal/privacy-policy">Data Retention in our Privacy Policy</a> for what happens to your
      information afterwards.
    </p>
    <p>We may suspend or terminate your account, in whole or in part, where we reasonably believe that:</p>
    <ul>
      <li>you have breached these Terms, our <a routerLink="/legal/community-guidelines">Community Guidelines</a>, or applicable law;</li>
      <li>your account poses a security, fraud, or safeguarding risk;</li>
      <li>we are required to do so by law, a regulator, or a competent authority; or</li>
      <li>continued provision of the Services to you is no longer commercially or operationally viable (for example, if we discontinue the Services generally).</li>
    </ul>
    <p>
      Where reasonably practicable and not prohibited by law or an ongoing investigation, we will aim to give you
      notice of suspension or termination and the reason for it. Sections of these Terms that by their nature should
      survive termination (including ownership, liability, and dispute resolution provisions) will continue to
      apply.
    </p>
  </section>

  <section id="disclaimers">
    <h2>Disclaimers</h2>
    <p>
      The Services are provided "as is" and "as available." To the fullest extent permitted by law, we do not make
      any warranty that the Services will be uninterrupted, error-free, or completely secure, or that content shared
      by other members is accurate, lawful, or appropriate.
    </p>
    <p>
      Nothing in this section excludes or limits any warranty implied by law that cannot lawfully be excluded,
      including under the Consumer Rights Act 2015 where you are a consumer.
    </p>
  </section>

  <section id="limitation-of-liability">
    <h2>Limitation of Liability</h2>
    <p>
      Nothing in these Terms limits or excludes our liability for death or personal injury caused by our negligence,
      for fraud or fraudulent misrepresentation, or for any other liability that cannot lawfully be limited or
      excluded under English law.
    </p>
    <p>
      Subject to the above, to the fullest extent permitted by law: (a) we will not be liable for any indirect or
      consequential loss arising from your use of the Services; and (b) our total liability to you arising out of or
      in connection with these Terms and your use of the Services will be limited to the fullest extent permitted by
      applicable law.
    </p>
    <p>
      We are not liable for the acts or omissions of other members, including content they upload or their conduct
      towards you, save to the extent required by applicable law.
    </p>
  </section>

  <section id="governing-law">
    <h2>Governing Law and Jurisdiction</h2>
    <p>These Terms, and any dispute arising out of or in connection with them or the Services, are governed by the laws of England and Wales.</p>
    <p>
      The courts of England and Wales will have exclusive jurisdiction over any such dispute, save that if you are a
      consumer resident elsewhere in the UK, mandatory local consumer protection law may entitle you to bring
      proceedings in your local courts instead.
    </p>
  </section>

  <section id="dispute-resolution">
    <h2>Dispute Resolution</h2>
    <p>
      If you have a complaint, please contact us first at the details in
      <a routerLink="/legal/privacy-policy">our Privacy Policy</a> so we can try to resolve it informally.
    </p>
    <p>Nothing in this section prevents either party from bringing a claim in the courts referred to in Section 8 (Governing Law and Jurisdiction).</p>
  </section>

</app-legal-page-layout>
